import { AfterViewInit, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { instanceToInstance } from 'class-transformer';
import { Observable, map } from 'rxjs';

import { environment } from '../../../environments/environment';
import { Login, PASSWORD_PATTERN } from '../../auth/models/login.model';
import { NotificationChannel, User } from '../../auth/models/user.model';
import { LoginService } from '../../auth/services/login.service';
import { CacheService } from '../../services/cache.service';
import { ComponentCommService } from '../../services/component-comm.service';
import { DataDogLoggerService } from '../../services/data-dog-logger.service';

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
})
export class UserProfileComponent implements OnInit, AfterViewInit {

  @ViewChild('infoTemplate') private readonly infoTemplate: TemplateRef<any>;
  @ViewChild('channelsTemplate') private readonly channelsTemplate: TemplateRef<any>;
  @ViewChild('passwordTemplate') private readonly passwordTemplate: TemplateRef<any>;
  @ViewChild('deleteTemplate') private readonly deleteTemplate: TemplateRef<any>;

  public errorMessage: string;
  public languages: any[];
  public successMessage: string;
  public sections: ProfileSection[];
  public selectedSection: ProfileSection;
  public password: {
    username?: string,
    password?: string,
    newPassword?: string,
    id?: number
  };
  public PASSWORD_PATTERN = PASSWORD_PATTERN;

  /**
   * Flag used to enable/disable UI buttons and links when an API request is in
   * progress.
   */
  public processing: boolean;
  public sectionSlug: string;
  public userTemp: User = {} as User;

  // Observables for async pipe
  public user$: Observable<User>;
  public userChannels$: Observable<NotificationChannel[]>;

  // Cached values
  private currentUser: User;

  constructor(
    private loginService: LoginService,
    private componentComm: ComponentCommService,
    private router: Router,
    private translateService: TranslateService,
    private route: ActivatedRoute,
    private cacheService: CacheService,
    private dataDogLoggerService: DataDogLoggerService,
    private cdr: ChangeDetectorRef
  ) {
    this.languages = environment.languages;
  }

  ngOnInit(): void {
    this.componentComm.emit({ name: 'app-title', title: 'GLOBAL.USER_PROFILE' });

    this.user$ = this.loginService.getCurrentUser().pipe(
      map(user => {
        this.currentUser = user;
        if (!user) {
          this.router.navigate(['login']);
        } else {
          // Schedule reset after change detection to avoid ExpressionChangedAfterItHasBeenCheckedError
          setTimeout(() => this.reset(), 0);
        }
        return user;
      })
    );

    // Load channels only when needed
    this.userChannels$ = this.loginService.getChannels().pipe(
      map(channels => channels.sort((a, b) => a.name.localeCompare(b.name)))
    );
  }

  ngAfterViewInit(): void {
    this.sections = [
      { id: 1, title: 'USER_PROFILE.PROFILE_TITLE', slug: 'info', template: this.infoTemplate },
      { id: 2, title: 'USER_PROFILE.NOTIFICATIONS', slug: 'channels', template: this.channelsTemplate },
      { id: 3, title: 'USER_PROFILE.SECURITY_TITLE', slug: 'password', template: this.passwordTemplate },
      { id: 4, title: 'USER_PROFILE.DELETE.BUTTON', slug: 'delete', template: this.deleteTemplate }
    ];

    this.route.paramMap.subscribe(params => {
      this.sectionSlug = params.get('section');
      this.setSection();
    });

    // Trigger change detection to avoid ExpressionChangedAfterItHasBeenCheckedError
    this.cdr.detectChanges();
  }

  private reset(): void {
    this.password = {};
    if (this.currentUser) {
      this.password.username = this.currentUser.email;
      this.password.id = this.currentUser.id;
    }

    this.userTemp = instanceToInstance(this.currentUser);

    this.errorMessage = undefined;
    this.successMessage = undefined;
  }

  public profileUpdate(): void {
    this.processing = true;
    this.errorMessage = undefined;
    this.successMessage = undefined;

    this.loginService.updateUser(this.userTemp).subscribe({
      next: user => {
        this.currentUser = this.userTemp;

        this.translateService.use(this.currentUser.language);
        this.successMessage = 'USER_PROFILE.PROFILE_UPDATED';

        let login: Login = this.cacheService.get('login');
        login.user.name = this.currentUser.name;
        login.user.last_name = this.currentUser.last_name;
        login.user.language = this.currentUser.language;
        this.cacheService.set('login', login, 60 * 24 * 365);

        this.processing = false;
      },
      error: error => {
        this.errorMessage = error;
        this.processing = false;
        this.dataDogLoggerService.warn(error.message, error.error);
      }
    });
  }

  public changePassword(): void {
    this.processing = true;
    this.errorMessage = undefined;
    this.successMessage = undefined;

    this.loginService.updatePassword(this.password).subscribe({
      next: change => {
        // this.reset();
        this.successMessage = 'USER_PROFILE.PASSWORD.UPDATED';
        this.processing = false;
      },
      error: error => {
        this.errorMessage = 'USER_PROFILE.PASSWORD.ERROR';
        this.processing = false;
        this.dataDogLoggerService.warn(error.message, error.error);
      }
    });
  }

  public delete(): void {
    this.processing = true;
    this.errorMessage = undefined;
    this.successMessage = undefined;

    this.loginService.delete(this.currentUser).subscribe({
      next: responnse => {
        console.log('responnse', responnse);
        this.loginService.logout();
        this.processing = false;
      },
      error: error => {
        this.errorMessage = 'USER_PROFILE.DELETE.ERROR';
        this.processing = false;
        this.dataDogLoggerService.warn(error.message, error.error);
      }
    });
  }

  private setSection(): void {
    // Set current collection
    this.selectedSection = this.sections.find(col => col.slug === this.sectionSlug);

    if (!this.selectedSection) {
      // Default to first
      this.navigateTo(this.sections[0]);
    } else this.reset();
  }

  public navigateTo(section: ProfileSection): void {
    if (!this.selectedSection ||
      this.selectedSection.slug !== section.slug) {

      setTimeout(() => {
        this.router.navigate(['/user', section.slug], {
          queryParams: section.queryParams || {}
        });
      });
    }
  }

  public updateChannels(userChannels: NotificationChannel[]): void {
    this.processing = true;
    this.loginService.updateChannels(userChannels).subscribe(() => {
      this.processing = false;
    });
  }
}

class ProfileSection {
  id: number;
  title: string;
  slug: string;
  template: TemplateRef<any>;
  queryParams?: any;
}